import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Autocomplete from "@mui/material/Autocomplete";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import ActionDialog from "components/admin/ActionDialog";
import GiftEditor from "components/admin/GiftEditor";
import SelectField from "components/admin/SelectField";
import { useConfirm } from "components/admin/Feedback";
import RequestEditor from "layouts/tasks/RequestEditor";
import { useFetch, useRunner } from "components/admin/hooks";
import api from "api/client";

const TYPE_RETIRED = 99;
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

function formOf(d) {
  return {
    name: d.name || "",
    description: d.description || "",
    type: d.type,
    fromNPC: d.fromNPC,
    task: d.task || [],
    gift: d.gift || [],
    taskNeed: d.taskNeed || [],
  };
}

/** Sửa một nhiệm vụ: mô tả, yêu cầu hoàn thành, phần thưởng, NPC giao, điều kiện mở. */
function TaskEditDialog({ taskId, meta, tasks, onClose, onSaved }) {
  const [run, busy] = useRunner();
  const confirm = useConfirm();
  const detail = useFetch(() => api.get(`/api/admin/tasks/${taskId}`), [taskId]);
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (detail.data) setForm(formOf(detail.data));
  }, [detail.data]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const before = detail.data ? formOf(detail.data) : null;
  const dirty = Boolean(form && before && !same(form, before));

  const typeName = (id) => (meta?.types || []).find((t) => t.id === id)?.name || `loại ${id}`;
  const npcName = (id) => (meta?.npcs || []).find((n) => n.id === id)?.name || `NPC ${id}`;
  const taskOptions = useMemo(
    () =>
      Object.values(tasks || {})
        .filter((t) => t.taskId !== taskId)
        .sort((a, b) => a.taskId - b.taskId),
    [tasks, taskId]
  );

  const changeList = () => {
    if (!dirty) return [];
    const out = [];
    if (form.name !== before.name) out.push(`• Tên: "${before.name}" → "${form.name}"`);
    if (form.type !== before.type)
      out.push(`• Loại: ${typeName(before.type)} → ${typeName(form.type)}`);
    if (form.fromNPC !== before.fromNPC)
      out.push(`• NPC giao: ${npcName(before.fromNPC)} → ${npcName(form.fromNPC)}`);
    if (form.description !== before.description) out.push("• Mô tả trong game");
    if (!same(form.task, before.task))
      out.push(`• Yêu cầu hoàn thành: ${before.task.length} dòng → ${form.task.length} dòng`);
    if (!same(form.gift, before.gift))
      out.push(`• Phần thưởng: ${before.gift.length} dòng → ${form.gift.length} dòng`);
    if (!same(form.taskNeed, before.taskNeed)) {
      const names = (ids) =>
        ids.length ? ids.map((id) => tasks?.[id]?.name || `#${id}`).join(", ") : "không cần";
      out.push(`• Cần xong trước: ${names(before.taskNeed)} → ${names(form.taskNeed)}`);
    }
    return out;
  };

  const submit = async () => {
    const doing = detail.data?.doingCount || 0;
    const ok = await confirm({
      title: "Lưu nhiệm vụ",
      message: [
        `Lưu thay đổi cho "${before.name}":`,
        "",
        ...changeList(),
        "",
        form.type === TYPE_RETIRED && before.type !== TYPE_RETIRED
          ? '• Loại "Đã ngừng": NPC sẽ không giao nhiệm vụ này cho ai nữa.'
          : null,
        form.task.length === 0 ? "• Không có yêu cầu nào: người chơi nhận là xong ngay." : null,
        doing > 0
          ? `• ${doing} người online đang làm dở vẫn làm theo bản cũ; bản mới áp dụng cho lần nhận sau.`
          : "• Người đang làm dở vẫn làm theo bản cũ; bản mới áp dụng cho lần nhận sau.",
      ]
        .filter((l) => l !== null)
        .join("\n"),
      confirmText: "Lưu",
    });
    if (!ok) return { ok: false };
    const res = await run(() => api.put(`/api/admin/tasks/${taskId}`, form), "Đã lưu nhiệm vụ");
    if (res.ok) {
      onSaved(res.result?.warning);
      onClose();
    }
    return res;
  };

  const close = async () => {
    if (dirty) {
      const ok = await confirm({
        title: "Bỏ thay đổi?",
        message: "Bạn đã sửa nhiệm vụ này nhưng chưa lưu. Đóng lại thì mất các thay đổi.",
        color: "warning",
        confirmText: "Bỏ thay đổi",
      });
      if (!ok) return;
    }
    onClose();
  };

  return (
    <ActionDialog
      open
      maxWidth="lg"
      title={
        <>
          Sửa nhiệm vụ: {detail.data?.name || "…"}
          <MDTypography component="span" variant="caption" color="text" ml={1}>
            #{taskId}
          </MDTypography>
        </>
      }
      submitText={dirty ? "Lưu…" : "Chưa có thay đổi"}
      busy={busy}
      disabled={!dirty}
      onClose={close}
      onSubmit={submit}
    >
      {detail.error && <Alert severity="error">{detail.error}</Alert>}
      {!form && !detail.error && <MDTypography variant="button">Đang tải…</MDTypography>}

      {form && (
        <MDBox>
          <Alert severity="info" sx={{ mb: 2 }}>
            Sửa ở đây <b>không</b> đổi tiến độ của người đang làm dở — họ vẫn làm theo bản lúc nhận.
            Thay đổi áp dụng cho lần nhận sau.
            {detail.data?.doingCount > 0 && (
              <>
                {" "}
                Hiện có <b>{detail.data.doingCount}</b> người online đang làm nhiệm vụ này.
              </>
            )}
          </Alert>

          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <MDInput
                fullWidth
                label="Tên nhiệm vụ"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <SelectField
                fullWidth
                label="Loại"
                value={form.type}
                onChange={(e) => set("type", Number(e.target.value))}
              >
                {(meta?.types || []).map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name}
                  </MenuItem>
                ))}
              </SelectField>
            </Grid>
            <Grid item xs={12}>
              <MDInput
                fullWidth
                multiline
                minRows={3}
                label="Mô tả (người chơi đọc trong game)"
                value={form.description}
                onChange={(e) => set("description", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SelectField
                fullWidth
                label="NPC giao nhiệm vụ"
                value={(meta?.npcs || []).some((n) => n.id === form.fromNPC) ? form.fromNPC : ""}
                onChange={(e) => set("fromNPC", Number(e.target.value))}
              >
                {(meta?.npcs || []).map((n) => (
                  <MenuItem key={n.id} value={n.id}>
                    {n.name}
                  </MenuItem>
                ))}
              </SelectField>
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                multiple
                options={taskOptions}
                value={form.taskNeed.map((id) => tasks?.[id] || { taskId: id, name: `#${id}` })}
                onChange={(e, v) =>
                  set(
                    "taskNeed",
                    v.map((t) => t.taskId)
                  )
                }
                getOptionLabel={(t) => t.name}
                isOptionEqualToValue={(a, b) => a.taskId === b.taskId}
                renderTags={(value, getTagProps) =>
                  value.map((t, i) => (
                    <Chip
                      size="small"
                      label={t.name}
                      {...getTagProps({ index: i })}
                      key={t.taskId}
                    />
                  ))
                }
                renderInput={(params) => (
                  <MDInput
                    {...params}
                    label="Cần xong trước (gõ tên nhiệm vụ)"
                    helperText="Để trống: ai cũng nhận được. Chỉ có tác dụng với nhiệm vụ chính tuyến."
                  />
                )}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />
          <RequestEditor value={form.task} onChange={(v) => set("task", v)} meta={meta} />

          <Divider sx={{ my: 3 }} />
          <GiftEditor
            value={form.gift}
            onChange={(v) => set("gift", v)}
            label="Phần thưởng"
            randomNote="Id âm trong dòng rút ngẫu nhiên là nhóm vật phẩm."
          />

          {dirty && (
            <Alert severity="warning" sx={{ mt: 3 }}>
              Có thay đổi chưa lưu:
              {changeList().map((l) => (
                <div key={l}>{l}</div>
              ))}
            </Alert>
          )}
        </MDBox>
      )}
    </ActionDialog>
  );
}

TaskEditDialog.propTypes = {
  taskId: PropTypes.number.isRequired,
  meta: PropTypes.object,
  /** map taskId -> nhiệm vụ rút gọn, để chọn "cần xong trước" theo tên. */
  tasks: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

export default TaskEditDialog;
